import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { IMAGES, SOCIALS } from "../../data/velvet";

const EASE = [0.16, 1, 0.3, 1];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.35 } },
};
const fadeItem = {
  hidden: { opacity: 0, y: 26 },
  show: { opacity: 1, y: 0, transition: { duration: 1, ease: EASE } },
};
const maskItem = {
  hidden: { y: "120%" },
  show: { y: "0%", transition: { duration: 1.25, ease: EASE } },
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="vt-vignette relative h-screen w-full overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={IMAGES.hero}
          alt="A vinyl record catching warm amber light in a darkened vintage recording studio"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-vt-black/55" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-vt-black via-vt-black/25 to-vt-black/70" />

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="relative z-10 flex h-full flex-col justify-center px-6 md:px-16 lg:px-24"
      >
        <motion.div variants={fadeItem} className="mb-7 flex items-center gap-4">
          <span className="h-px w-10 bg-vt-gold/70" />
          <span className="font-mono text-[10px] uppercase tracking-[0.36em] text-vt-gold/90 md:text-xs">
            A Timeless Soul Universe · Old Lantern Records
          </span>
        </motion.div>

        <h1 className="font-serif font-light tracking-tight text-vt-cream">
          <span className="block overflow-hidden pb-[0.16em]">
            <motion.span
              variants={maskItem}
              className="block text-6xl leading-[0.98] sm:text-7xl md:text-8xl lg:text-[9rem]"
            >
              The Velvet
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-[0.18em]">
            <motion.span
              variants={maskItem}
              className="block text-6xl italic leading-[1.02] text-vt-gold sm:text-7xl md:text-8xl lg:text-[9rem]"
            >
              Testament
            </motion.span>
          </span>
        </h1>

        <motion.p
          variants={fadeItem}
          className="mt-8 max-w-xl font-body text-base font-light leading-relaxed tracking-wide text-vt-ash md:text-lg"
        >
          Timeless soul. Original stories. Recurring voices. A soul universe
          built around original stories and recurring voices — where every voice
          leaves a testament.
        </motion.p>

        <motion.div variants={fadeItem} className="mt-11 flex flex-wrap items-center gap-4">
          <a
            data-testid="hero-spotify-btn"
            href={SOCIALS.spotify}
            target="_blank"
            rel="noreferrer"
            className="group inline-flex items-center gap-3 rounded-full bg-vt-gold px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-vt-black transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-vt-cream"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.52 17.34c-.24.36-.66.48-1.02.24-2.82-1.74-6.36-2.1-10.56-1.14-.42.12-.78-.18-.9-.54-.12-.42.18-.78.54-.9 4.56-1.02 8.52-.6 11.64 1.32.42.18.48.66.3 1.02zm1.44-3.3c-.3.42-.84.6-1.26.3-3.24-1.98-8.16-2.58-11.94-1.38-.48.12-1.02-.12-1.14-.6-.12-.48.12-1.02.6-1.14 4.38-1.32 9.78-.66 13.5 1.62.36.18.54.78.24 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.3c-.6.18-1.2-.18-1.38-.72-.18-.6.18-1.2.72-1.38 4.26-1.26 11.28-1.02 15.72 1.62.54.3.72 1.02.42 1.56-.3.42-1.02.6-1.56.3z" />
            </svg>
            Listen on Spotify
          </a>
          <a
            data-testid="hero-explore-btn"
            href="#voices"
            onClick={(e) => {
              e.preventDefault();
              window.__lenis
                ? window.__lenis.scrollTo("#voices")
                : document.querySelector("#voices")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="inline-flex items-center gap-3 rounded-full border border-vt-gold/50 px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] text-vt-cream transition-colors duration-300 hover:border-vt-gold hover:text-vt-gold"
          >
            Meet the Voices
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2"
      >
        <motion.span variants={fadeItem} className="font-mono text-[9px] uppercase tracking-[0.3em] text-vt-ash">
          Scroll
        </motion.span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ArrowDown size={16} className="text-vt-gold" />
        </motion.div>
      </motion.div>
    </section>
  );
};
